import type { Creature } from '@/domain/creature/types';
import { normalizeCareProfile } from './profile';
import { deriveTemperament, hasTemperament, type TemperamentProfile } from './temperament';

export type BehaviorMood =
  | 'content'
  | 'hungry'
  | 'lonely'
  | 'dirty'
  | 'sick'
  | 'sleepy'
  | 'tantrum'
  | 'bored'
  | 'overfed';

export type PreferredAction = 'feed' | 'play' | 'clean' | 'heal' | 'discipline' | 'sleep' | 'none';

export interface BehaviorProfile {
  mood: BehaviorMood;
  preferredAction: PreferredAction;
  temperament: TemperamentProfile;
  behaviorTags: string[];
}

export function resolveBehaviorProfile(creature: Creature, opts: { sleeping?: boolean } = {}): BehaviorProfile {
  const temperament = deriveTemperament(creature.genome);
  const care = normalizeCareProfile(creature.careProfile);
  const tags: string[] = [`temperament:${temperament.primary}`];

  if (opts.sleeping) return { mood: 'sleepy', preferredAction: 'sleep', temperament, behaviorTags: [...tags, 'sleeping'] };
  if (creature.state.tantrum) {
    return { mood: 'tantrum', preferredAction: 'discipline', temperament, behaviorTags: [...tags, 'needs-discipline'] };
  }
  if (creature.state.sick) {
    if (hasTemperament(temperament, 'fragile')) tags.push('fragile-sick');
    return { mood: 'sick', preferredAction: 'heal', temperament, behaviorTags: tags };
  }

  const dirtyThreshold = hasTemperament(temperament, 'tidy') ? 1 : 2;
  if (creature.state.poopCount >= dirtyThreshold) {
    return { mood: 'dirty', preferredAction: 'clean', temperament, behaviorTags: [...tags, 'dirty'] };
  }

  if (creature.state.hunger <= 25) {
    return { mood: 'hungry', preferredAction: 'feed', temperament, behaviorTags: [...tags, 'hungry'] };
  }
  if (creature.state.weight >= 80) {
    return { mood: 'overfed', preferredAction: 'play', temperament, behaviorTags: [...tags, 'overfed'] };
  }

  const lonelyThreshold = hasTemperament(temperament, 'playful') || hasTemperament(temperament, 'needy') ? 45 : 35;
  if (creature.state.happiness <= lonelyThreshold) {
    return { mood: 'lonely', preferredAction: 'play', temperament, behaviorTags: [...tags, 'lonely'] };
  }

  if (care.plays === 0 && creature.lifecycle.stage !== 'egg') {
    return { mood: 'bored', preferredAction: 'play', temperament, behaviorTags: [...tags, 'bored'] };
  }

  return { mood: 'content', preferredAction: 'none', temperament, behaviorTags: tags };
}
